// @ts-nocheck
import { Icon } from "@shared/antd/icon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ExtTaskData } from "../../../cuba/entities/base/tsadv_ExtTaskData";
import { flatMap } from "app/shared/lodash/lodash";
import { TableAdaptive } from "./ui/table-adaptive";
import { useMediaQuery } from "react-responsive";
import { getTaskColumns } from "../../pages/Bproc/TaskData/fields";
import { NestedTaskTable } from "./ui/nested-task-table";

export type ExtTaskDataChild = {
  childs: ExtTaskDataChild[];
  key?: string;
  isOpened?: boolean;
  level?: number;
} & ExtTaskData

interface Props {
  data: any[];
}

const requestStatusColor = {
  START: "green-400",
  SEND_FOR_APPROVAL: "green-400",
  APPROVE: "green-400",
  REJECTED: "red-400",
  CANCEL: "red-400",
  REVISION: "orange-400",
  TASK_CLOSED: "gray-500",
};


const HistoryApproverTable: React.FC<Props> = React.memo(({ data }) => {
  const intl = useIntl();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [openedArray, setOpenedArray] = useState<ExtTaskDataChild[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const handleExpand = useCallback((expanded: boolean, record: ExtTaskDataChild) => {
    setExpandedRowKeys(prev => expanded
      ? [...prev, record.key!]
      : prev.filter(k => k !== record.key),
    );
  }, []);

  const toggleIsOpened = useCallback((key: string) => {
    const updateNodes = (nodes: ExtTaskDataChild[]): ExtTaskDataChild[] => {
      return nodes.map(node => ({
        ...node,
        isOpened: node.key === key ? !node.isOpened : node.isOpened,
        childs: node.childs ? updateNodes(node.childs) : [],
      }));
    };

    setOpenedArray(prev => updateNodes(prev));
  }, []);

  const columns = useMemo(() => getTaskColumns(intl, requestStatusColor, toggleIsOpened), [intl]);

  const flattenTree = (nodes: ExtTaskDataChild[]): ExtTaskDataChild[] => {
    return flatMap(nodes, node => {
      const children = node.childs ? flattenTree(node.childs) : [];
      return [node, ...children];
    });
  };

  useEffect(() => {
    const addKeysAndLevels = (nodes: ExtTaskDataChild[], parentKey = "nested", level = 1): ExtTaskDataChild[] =>
      nodes.map((node, i) => {
        const key = `${parentKey}-${i}`;
        return {
          ...node,
          key,
          level,
          isOpened: true,
          childs: node.childs ? addKeysAndLevels(node.childs, key, level + 1) : [],
        };
      });

    const collectKeys = (nodes: ExtTaskDataChild[]): string[] =>
      flatMap(nodes, node =>
        node.childs?.length ? [node.key!, ...collectKeys(node.childs)] : [],
      );

    const processed = isMobile ? addKeysAndLevels(data) : addKeysAndLevels(data);

    setOpenedArray(processed);
    setExpandedRowKeys(collectKeys(processed));
  }, [data, isMobile]);

  const renderNestedTable = useCallback(
    (record: ExtTaskDataChild) => (
      <NestedTaskTable
        record={record}
        columns={columns}
        isMobile={isMobile}
        toggleIsOpened={toggleIsOpened}
        requestStatusColor={requestStatusColor}
      />
    ),
    [columns, isMobile, toggleIsOpened, setOpenedArray, requestStatusColor],
  );

  return (
    <div className="collapse-users-table">
      <TableAdaptive
        columns={columns}
        dataSource={openedArray}
        pagination={false}
        isAdaptive={isMobile}
        bordered
        rowKey="key"
        expandedRowRender={renderNestedTable}
        expandIconColumnIndex={0}
        expandedRowKeys={expandedRowKeys}
        onExpand={handleExpand}
        onExpandChild={toggleIsOpened}
        expandIcon={({ expanded, onExpand, record }) =>
          record.childs?.length ? (
            <a onClick={e => onExpand(record, e)} style={{ marginRight: 8 }}>
              <Icon type={expanded ? "caret-up" : "caret-down"} />
            </a>
          ) : null
        }
      />
    </div>
  );
});

export default HistoryApproverTable;
